import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Card,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import PropTypes from 'prop-types';
import ArgonTypography from 'components/ArgonTypography';
import { styled } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNotifications } from 'context/NotificationContext';

import { fetchSalesforceStateForDeploy, compareForDeploy } from 'services/productDataService';

import ProductDetailsReview from '../../commit/reviewComponents/ProductDetailsReview';
import ComponentsReview from '../../commit/reviewComponents/ComponentsReview';
import RevenueAttributes from '../../commit/reviewComponents/RevenueAttributes';
import CPQAttributesReview from '../../commit/reviewComponents/CPQAttributesReview';
import ProductRulesReview from '../../commit/reviewComponents/ProductRulesReview';
import ConsumptionScheduleReview from '../../commit/reviewComponents/ConsumptionScheduleReview';
import PricingModelReview from '../../commit/reviewComponents/PricingModelReview';
import PricebooksReview from '../../commit/reviewComponents/PricebooksReview';
import FulfillmentReview from '../../commit/reviewComponents/FulfillmentReview';
import DecompositionReview from '../../commit/reviewComponents/DecompositionReview';

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  '&.MuiToggleButton-root': {
    border: 'none',
    background: 'transparent',
    '&.Mui-selected': {
      backgroundColor: theme.palette.action.selected,
      '&:hover': {
        backgroundColor: theme.palette.action.selected,
      },
    },
  },
}));

function DeployReview({ org, orgId, isRevenue, selectedProducts, onReviewComplete }) {
  const { addNotification } = useNotifications();
  const [processedDiffs, setProcessedDiffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSameFields, setShowSameFields] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');

  // --- FIX #2: WRAP FUNCTIONS IN useCallback TO STABILIZE THEIR REFERENCE ---
  // This prevents the main useEffect from re-running unnecessarily.
  const stableOnReviewComplete = useCallback(onReviewComplete, []);
  const stableAddNotification = useCallback(addNotification, []);

  useEffect(() => {
    const processDataForReview = async () => {
      // Guard against running with invalid props
      if (!orgId || !selectedProducts) {
        setLoading(false);
        return;
      }
      // If the array is empty, we can stop early.
      if (selectedProducts.length === 0) {
        setLoading(false);
        setProcessedDiffs([]);
        stableOnReviewComplete([]);
        return;
      }

      setLoading(true);
      setError(null);
      setProcessedDiffs([]);

      try {
        const { sfData, fetchErrors } = await fetchSalesforceStateForDeploy(orgId, selectedProducts, isRevenue);

        if (fetchErrors.length > 0) {
          fetchErrors.forEach((err) => {
            stableAddNotification({
              title: ['Warning', `Fetch Failed: ${err.endpoint}`],
              severity: 'warning',
              details: err.reason,
            });
          });
        }

        console.log('[sfData]', sfData);

        const { diffs, errors: comparisonErrors } = await compareForDeploy(
          selectedProducts,
          sfData,
          orgId || org.org_id
        );

        console.log('[diffs]', diffs);
        if (comparisonErrors.length > 0) {
          const errorDetails = comparisonErrors.map((e) => e.message || String(e)).join('; ');
          stableAddNotification({
            title: ['Warning', 'Comparison Failed'],
            severity: 'warning',
            details: errorDetails,
          });
          setSnackbarMessage('Some products could not be compared. See notifications for details.');
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);
        }

        setProcessedDiffs(diffs);

        // For the final deploy stage, we need the full product object from our database,
        // which is the "source of truth" for the deployment. This is now correctly extracted.
        const productsForCommitStage = diffs.map((diff) => diff.product).filter(Boolean);
        stableOnReviewComplete(productsForCommitStage);
      } catch (err) {
        const message = err.message || 'An unexpected error occurred during the review process.';
        setError(message);
        stableAddNotification({ title: ['Error', 'Review Failed'], severity: 'error', details: message });
      } finally {
        setLoading(false);
      }
    };

    processDataForReview();
    // The dependency array now uses the stable function references.
  }, [orgId, selectedProducts, isRevenue, stableAddNotification, stableOnReviewComplete]);

  const tabData = useMemo(
    () => [
      { label: 'Product Details', key: 'productDetails' },
      { label: 'Components', key: 'components' },
      ...(isRevenue
        ? [{ label: 'Attributes', key: 'revenueAttributes' }]
        : [
            { label: 'Product Rules', key: 'productRules' },
            { label: 'Rate Cards', key: 'rateCards' },
            { label: 'Attributes', key: 'cpqAttributes' },
          ]),
      ...(isRevenue
        ? [
            { label: 'Decomposition', key: 'decomposition' },
            { label: 'Fulfillment', key: 'fulfillment' },
          ]
        : []),
      { label: 'Pricing Model', key: 'pricingModel' },
      { label: 'Pricebooks', key: 'pricebooks' },
    ],
    [isRevenue]
  );

  const [activeReviewStage, setActiveReviewStage] = useState('productDetails');
  const handleTabChange = (event, newValue) => setActiveReviewStage(newValue);
  const handleShowSameChange = (event, newValue) => {
    if (newValue !== null) setShowSameFields(newValue);
  };
  const handleSnackbarClose = (event, reason) => {
    if (reason !== 'clickaway') setSnackbarOpen(false);
  };

  const renderReviewContent = () => {
    const commonProps = { selectedOrg: org, productData: processedDiffs, showSameFields };
    switch (activeReviewStage) {
      case 'productDetails':
        return <ProductDetailsReview {...commonProps} />;
      case 'components':
        return <ComponentsReview {...commonProps} />;
      case 'revenueAttributes':
        return <RevenueAttributes {...commonProps} />;
      case 'cpqAttributes':
        return <CPQAttributesReview {...commonProps} />;
      case 'productRules':
        return <ProductRulesReview {...commonProps} />;
      case 'rateCards':
        return <ConsumptionScheduleReview {...commonProps} />;
      case 'fulfillment':
        return <FulfillmentReview {...commonProps} />;
      case 'decomposition':
        return <DecompositionReview {...commonProps} />;
      case 'pricingModel':
        return <PricingModelReview {...commonProps} />;
      case 'pricebooks':
        return <PricebooksReview {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <ArgonBox py={3}>
      <Card>
        <ArgonBox p={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {/* --- FIX #1: ADD variant="fullWidth" and sx TO THE TABS --- */}
            <Tabs
              value={activeReviewStage}
              onChange={handleTabChange}
              aria-label="review-tabs"
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
              sx={{ flexGrow: 1, marginRight: 2 }}
            >
              {tabData.map((tab) => (
                <Tab key={tab.key} label={tab.label} value={tab.key} />
              ))}
            </Tabs>
            <ToggleButtonGroup value={showSameFields} exclusive onChange={handleShowSameChange} size="small">
              <StyledToggleButton value={false}>
                <VisibilityOffIcon />
              </StyledToggleButton>
              <StyledToggleButton value={true}>
                <VisibilityIcon />
              </StyledToggleButton>
            </ToggleButtonGroup>
          </Box>
          <ArgonBox mt={3}>
            <Card>
              {loading ? (
                <ArgonBox p={3} display="flex" justifyContent="center" minHeight="200px">
                  <CircularProgress color="info" />
                </ArgonBox>
              ) : error ? (
                <ArgonBox p={3} minHeight="200px">
                  <ArgonTypography color="error">{error}</ArgonTypography>
                </ArgonBox>
              ) : (
                renderReviewContent()
              )}
            </Card>
          </ArgonBox>
        </ArgonBox>
      </Card>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} variant="filled" severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ArgonBox>
  );
}

DeployReview.propTypes = {
  org: PropTypes.object.isRequired,
  orgId: PropTypes.string, // Can be null initially
  isRevenue: PropTypes.bool,
  selectedProducts: PropTypes.array.isRequired,
  onReviewComplete: PropTypes.func.isRequired,
};

// Add defaultProps for safety
DeployReview.defaultProps = {
  isRevenue: false,
  orgId: null,
};

export default DeployReview;
